import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Phone, ClipboardList, CheckCircle, ArrowRight } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ServiceGallery } from '../components/ServiceGallery';

export const RoofReplacementPage = () => {
  useEffect(() => {
    // SEO: Set page title and meta description (Priority 8)
    document.title = "Roof Replacement Sydney | Metal Roofing Specialists | Dezire Metal Roofing";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Upgrade your home with professional roof replacement services in Greater Sydney. Dezire Metal Roofing provides quality metal roofing solutions and free quotes.');
    }
    window.scrollTo(0, 0);
  }, []);

  const whenToReplace = [
    "Your roof has widespread wear or corrosion",
    "Repairs are becoming frequent",
    "Storm damage is extensive",
    "The roof no longer performs reliably",
    "You want to improve the appearance and value of your home"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="page-title">
              Roof Replacement <span className="text-primary">Sydney</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              If your roof is old, badly worn, rusted, or repeatedly causing problems, a full roof replacement may be the most cost-effective long-term solution. Dezire Metal Roofing provides professional roof replacement services across Greater Sydney, helping homeowners upgrade to safer, stronger, and better-looking roofing systems.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book-inspection">
                <Button size="lg" className="bg-primary hover:bg-accent text-white" data-testid="hero-cta-btn">
                  <ClipboardList className="w-5 h-5 mr-2" />
                  Book a Free Inspection
                </Button>
              </Link>
              <a href="/call">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900">
                  <Phone className="w-5 h-5 mr-2" />
                  Call 0433 973 844
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                When to Consider Roof Replacement
              </h2>
              <ul className="space-y-4 mb-8">
                {whenToReplace.map((reason, index) => (
                  <li key={`replace-reason-${index}-${reason.slice(0, 10)}`} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 text-lg">{reason}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-primary/10 p-6 rounded-xl">
                <h3 className="font-bold text-gray-900 mb-3">Our Approach</h3>
                <p className="text-gray-700">
                  We assess the condition of the existing roof, explain your options clearly, and deliver replacement work with attention to finish, performance, and durability.
                </p>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="bg-gray-50 p-8 rounded-xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Repair or Replace?
                </h2>
                <p className="text-gray-600 mb-6">
                  Not sure whether your roof needs repair or full replacement? We can help you decide. Book a free inspection and we&apos;ll assess your roof&apos;s condition and explain the most practical option for your situation.
                </p>

                <Link to="/book-inspection">
                  <Button className="w-full bg-primary hover:bg-accent text-white mb-4" data-testid="content-cta-btn">
                    Book a Free Inspection
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                
                <p className="text-center text-gray-500 text-sm">
                  Or call us directly: <a href="/call" className="text-primary font-semibold">0433 973 844</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <ServiceGallery service="roof-replacement" />

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Related Services
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/services/metal-roof-installation" className="bg-gray-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-gray-900 mb-2">Metal Roof Installation</h3>
              <p className="text-gray-600 text-sm">New roof installations</p>
            </Link>
            <Link to="/services/roof-repairs" className="bg-gray-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-gray-900 mb-2">Roof Repairs</h3>
              <p className="text-gray-600 text-sm">Fix leaks and damage</p>
            </Link>
            <Link to="/services/gutter-installation" className="bg-gray-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-gray-900 mb-2">Gutter Installation</h3>
              <p className="text-gray-600 text-sm">Gutters and drainage</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RoofReplacementPage;
